/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import Button from 'react-bootstrap/Button'
import { Formik, Form, Field } from 'formik'
import { useSubmitShippingVerificationMutation } from 'store/services/patientEndpoints'
import * as Yup from 'yup'

import states from 'constants/states'
import { phoneValidation, requiredPhoneValidation } from 'helpers/validate'
import MaskInput from 'components/atoms/MaskInput'
import GoogleAutocomplete from 'components/atoms/GoogleAutocomplete'
import { TextAreaInput } from 'components/atoms/Textarea'
import { phoneNumberMask, zipMask } from 'helpers/masks'
import { getAddressFields } from 'components/dme/forms/utils/userFormUtils'
import * as generalHelper from 'helpers/generalHelper'
import { Order } from 'store/types'

interface IProps {
  nextStep: () => void
  order: Order | undefined
}

const formValidation = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  homePhone: requiredPhoneValidation,
  mobilePhone: phoneValidation,
  primaryAddressStreetLine1: Yup.string().required('Required'),
  primaryAddressCity: Yup.string().required('Required'),
  primaryAddressState: Yup.string().required('Required'),
  primaryAddressZip: Yup.string().required('Required'),
  smartphone: Yup.string().required('Required'),
  primaryInsuranceMemberId: Yup.string().required('Required'),
})

const PatientShippingInformationVerification = ({ nextStep, order }: IProps) => {
  const [submitShippingVerification] = useSubmitShippingVerificationMutation()

  const initialData = useMemo(
    () => ({
      firstName: order?.patient?.firstName ?? '',
      lastName: order?.patient?.lastName ?? '',
      middleName: order?.patient?.middleName ?? '',
      homePhone: order?.patient?.homePhone.replace(/^\+1/, '') ?? '',
      mobilePhone: order?.patient?.mobilePhone?.replace(/^\+1/, '') ?? '',
      email: order?.patient?.email ?? '',
      address: order?.patient?.deliverySameAsPrimary
        ? order?.patient?.primaryAddressStreetLine1 ?? ''
        : order?.patient?.deliveryAddressStreetLine1 ??
          order?.patient?.primaryAddressStreetLine1 ??
          '',
      primaryAddressStreetLine1: order?.patient?.deliverySameAsPrimary
        ? order?.patient?.primaryAddressStreetLine1 ?? ''
        : order?.patient?.deliveryAddressStreetLine1 ??
          order?.patient?.primaryAddressStreetLine1 ??
          '',
      primaryAddressStreetLine2: order?.patient?.deliverySameAsPrimary
        ? order?.patient?.primaryAddressStreetLine2 ?? ''
        : order?.patient?.deliveryAddressStreetLine2 ??
          order?.patient?.primaryAddressStreetLine2 ??
          '',
      primaryAddressCity: order?.patient?.deliverySameAsPrimary
        ? order?.patient?.primaryAddressCity ?? ''
        : order?.patient?.deliveryAddressCity ?? order?.patient?.primaryAddressCity ?? '',
      primaryAddressState: order?.patient?.deliverySameAsPrimary
        ? order?.patient?.primaryAddressState ?? ''
        : order?.patient?.deliveryAddressState ?? order?.patient?.primaryAddressState ?? '',
      primaryAddressZip: order?.patient?.deliverySameAsPrimary
        ? order?.patient?.primaryAddressZip ?? ''
        : order?.patient?.deliveryAddressZip ?? order?.patient?.primaryAddressZip ?? '',
      smartphone: '',
      comments: '',
      primaryInsuranceMemberId: order?.payers?.find((x) => x.position === 1)?.memberId || '',
      primaryInsuranceGroupNumber: order?.payers?.find((x) => x.position === 1)?.groupId || '',
      secondaryInsuranceMemberId: order?.payers?.find((x) => x.position === 2)?.memberId || '',
      secondaryInsuranceGroupNumber: order?.payers?.find((x) => x.position === 2)?.groupId || '',
      tertiaryInsuranceMemberId: order?.payers?.find((x) => x.position === 3)?.memberId || '',
      tertiaryInsuranceGroupNumber: order?.payers?.find((x) => x.position === 3)?.groupId || '',
    }),
    [order],
  )

  return (
    <div className="container" style={{ maxWidth: 720 }}>
      <div className="mb-3 pb-3">
        <h2>Pulse-Oximetry (POX) - Shipping information verification</h2>
        <div className="mt-5 mb-5 pt-3 border-top">
          <p>
            Please review and verify your shipping information before proceeding with the testing.
          </p>
        </div>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidation}
          initialValues={initialData}
          onSubmit={async (values) => {
            const normalize = (obj: Record<string, string>) => {
              const { smartphone, ...rest } = obj

              return {
                ...rest,
                homePhone: obj.homePhone?.replace(/\D/g, '') ?? '',
                mobilePhone: obj.mobilePhone?.replace(/\D/g, '') ?? '',
              }
            }

            const sanitizedValues = normalize(values)
            const sanitizedInitialData = normalize(initialData)

            const editSuggested =
              JSON.stringify(sanitizedValues) !== JSON.stringify(sanitizedInitialData)

            if (!order) {
              return
            }

            try {
              await submitShippingVerification({
                orderId: Number(order.id),
                ...values,
                editSuggested,
              })
              nextStep()
            } catch (err) {}
          }}>
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form id="patientDetails">
              <div className={`container`}>
                <div className="row">
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="firstName">
                      First Name
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="firstName"
                      className="form-control"
                      placeholder="First Name"
                      value={values.firstName}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                    {touched.firstName && errors.firstName ? (
                      <div className="text-danger">{errors.firstName}</div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="col-sm-12 col-lg-2 form-group textGray">
                    <label htmlFor="middleName">Middle Name</label>
                    <Field
                      type="text"
                      name="middleName"
                      className="form-control"
                      placeholder="Middle Name"
                      value={values.middleName}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                  </div>
                  <div className="col-sm-9 col-lg-4 form-group textGray">
                    <label htmlFor="lastName">
                      Last Name
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="lastName"
                      className="form-control"
                      placeholder="Last Name"
                      value={values.lastName}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                    {touched.lastName && errors.lastName ? (
                      <div className="text-danger">{errors.lastName}</div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="homePhone">
                      Home Number
                      <span className="text-danger">*</span>
                    </label>
                    <MaskInput
                      value={values.homePhone}
                      onChange={handleChange}
                      name="homePhone"
                      mask={phoneNumberMask}
                      placeholder="(###) ###-####"
                      autoComplete="off"
                    />
                    {touched.homePhone && errors.homePhone ? (
                      <div className="text-danger">{errors.homePhone}</div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="mobilePhone">Mobile Number</label>
                    <MaskInput
                      value={values.mobilePhone}
                      onChange={handleChange}
                      name="mobilePhone"
                      mask={phoneNumberMask}
                      placeholder="(###) ###-####"
                      autoComplete="off"
                    />
                  </div>
                  <div className="col-sm-9 col-lg-4 form-group textGray">
                    <label htmlFor="email">Email</label>
                    <Field
                      type="text"
                      name="email"
                      className="form-control"
                      placeholder="Email"
                      value={values.email}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                  </div>
                </div>
              </div>

              <div className={`container`}>
                <div className="row">
                  <div className="col-sm-12 col-lg-6">
                    <div className="row">
                      <div className="col-12 form-group textGray">
                        <label htmlFor="primaryAddressStreetLine1">
                          {generalHelper.t('Shipping Address')}
                          <span className="text-danger">*</span>
                        </label>
                        <GoogleAutocomplete
                          id="address"
                          name="address"
                          onInputChanged={(v) => setFieldValue('primaryAddressStreetLine1', v)}
                          onChange={(place) => {
                            const { address, city, state, zip } = getAddressFields(place, 'address')
                            console.log(address, city, state, zip)
                            setFieldValue('primaryAddressStreetLine1', address)
                            setFieldValue('primaryAddressCity', city)
                            setFieldValue('primaryAddressState', state)
                            setFieldValue('primaryAddressZip', zip)
                          }}
                        />
                        {touched.primaryAddressStreetLine1 && errors.primaryAddressStreetLine1 ? (
                          <div className="text-danger">{errors.primaryAddressStreetLine1}</div>
                        ) : null}
                      </div>
                      <div className="col-12 form-group textGray">
                        <Field
                          type="text"
                          name="primaryAddressStreetLine2"
                          className="form-control"
                          placeholder="Suite / Apt"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-4 form-group textGray">
                        <label htmlFor="primaryAddressCity">
                          City
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="primaryAddressCity"
                          className="form-control"
                          placeholder="City"
                        />
                        {touched.primaryAddressCity && errors.primaryAddressCity ? (
                          <div className="text-danger">{errors.primaryAddressCity}</div>
                        ) : null}
                      </div>
                      <div className="col-4 form-group textGray">
                        <label htmlFor="primaryAddressState">
                          State
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          className="form-control"
                          as="select"
                          name="primaryAddressState"
                          placeholder="State">
                          {states &&
                            states.map((s, i) => (
                              <option key={i} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                        </Field>
                        {touched.primaryAddressState && errors.primaryAddressState ? (
                          <div className="text-danger">{errors.primaryAddressState}</div>
                        ) : null}
                      </div>
                      <div className="col-4 form-group textGray">
                        <label htmlFor="primaryAddressZip">
                          Zip Code
                          <span className="text-danger">*</span>
                        </label>
                        <MaskInput
                          value={values.primaryAddressZip}
                          onChange={handleChange}
                          name="primaryAddressZip"
                          mask={zipMask}
                          placeholder="#####-####"
                        />

                        {touched.primaryAddressZip && errors.primaryAddressZip ? (
                          <div className="text-danger">{errors.primaryAddressZip}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`container`}>
                <div className="row">
                  <div className="col-12 form-group textGray">
                    <Field
                      component={TextAreaInput}
                      name="comments"
                      label="Comments / Special Instructions"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              <div className="col-sm-12 col-lg-4 form-group textGray">
                <label htmlFor="smartphone">
                  Do you have a smartphone?
                  <span className="text-danger">*</span>
                </label>
                <Field
                  className="form-control"
                  as="select"
                  name="smartphone"
                  value={values.smartphone}
                  onChange={handleChange}>
                  <option value="">Select...</option>
                  <option value="iphone">iPhone</option>
                  <option value="android">Android</option>
                  <option value="none">No smart phone</option>
                </Field>
                {touched.smartphone && errors.smartphone ? (
                  <div className="text-danger">{errors.smartphone}</div>
                ) : null}
              </div>
              <div className="px-2">
                <h4>Insurance</h4>
                <div className="mt-3">
                  <h5>
                    Primary{' '}
                    <span className="text-gray font-weight-medium">
                      ({order?.payers?.find((x) => x.position === 1)?.name})
                    </span>
                  </h5>
                  <div className="row">
                    <div className="col-4 form-group textGray">
                      <label htmlFor="primaryInsuranceMemberId">
                        Member ID
                        <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        name="primaryInsuranceMemberId"
                        className="form-control"
                        placeholder="Member ID"
                      />
                      {touched.primaryInsuranceMemberId && errors.primaryInsuranceMemberId ? (
                        <div className="text-danger">{errors.primaryInsuranceMemberId}</div>
                      ) : null}
                    </div>
                    <div className="col-4 form-group textGray">
                      <label htmlFor="primaryInsuranceGroupNumber">Group ID</label>
                      <Field
                        type="text"
                        name="primaryInsuranceGroupNumber"
                        className="form-control"
                        placeholder="Group ID"
                      />
                    </div>
                  </div>
                </div>
                {values?.secondaryInsuranceMemberId && (
                  <div className="mt-3">
                    <h5>
                      Secondary{' '}
                      <span className="text-gray font-weight-medium">
                        ({order?.payers?.find((x) => x.position === 2)?.name})
                      </span>
                    </h5>
                    <div className="row">
                      <div className="col-4 form-group textGray">
                        <label htmlFor="secondaryInsuranceMemberId">Member ID</label>
                        <Field
                          type="text"
                          name="secondaryInsuranceMemberId"
                          className="form-control"
                          placeholder="Member ID"
                        />
                      </div>
                      <div className="col-4 form-group textGray">
                        <label htmlFor="secondaryInsuranceGroupNumber">Group ID</label>
                        <Field
                          type="text"
                          name="secondaryInsuranceGroupNumber"
                          className="form-control"
                          placeholder="Group ID"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {values?.tertiaryInsuranceMemberId && (
                  <div className="mt-3">
                    <h5>
                      Tertiary{' '}
                      <span className="text-gray font-weight-medium">
                        ({order?.payers?.find((x) => x.position === 3)?.name})
                      </span>
                    </h5>
                    <div className="row">
                      <div className="col-4 form-group textGray">
                        <label htmlFor="tertiaryInsuranceMemberId">Member ID</label>
                        <Field
                          type="text"
                          name="tertiaryInsuranceMemberId"
                          className="form-control"
                          placeholder="Member ID"
                        />
                      </div>
                      <div className="col-4 form-group textGray">
                        <label htmlFor="tertiaryInsuranceGroupNumber">Group ID</label>
                        <Field
                          type="text"
                          name="tertiaryInsuranceGroupNumber"
                          className="form-control"
                          placeholder="Group ID"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="primary" type="submit" css={{}}>
                  Submit
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default PatientShippingInformationVerification
